import { classnames } from 'Utils/classnames/classnames.utils';
import cookies from 'js-cookie';
import { omit } from 'lodash';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { Fragment, memo, useEffect, useRef, useState } from 'react';
import URI from 'urijs';
import { useSelector } from 'react-redux';
import { userStatus } from 'Tracking/tracking-api';
import { CA_TRACKING } from 'Tracking/constants';
import config from 'Config';
import { useAppContext } from 'Context/ReactAppContext';
import { usePageContext } from 'Context/PageContext';
import { userInfoWrapper } from 'Store/user-info/hoc/UserInfo';
import { addTranslationKeys } from 'Src/i18n';
import { getFromLocalStorage, setInLocalStorage } from 'Client/local-storage-api';
import { TOASTERS } from 'Components/frameworks/portal/Portal.constants';
import { CrisisAlertLinkBanner } from 'Components/widgets/crisis-alert/CrisisAlertLinkBanner';
import { DataScore } from 'Components/widgets/data-score/DataScore';
import { FTCToasterContainer } from 'Components/widgets/ftc-toaster/FTCToasterContainer';
import { PriceRemoteMaximumFavoriteToaster } from 'Components/widgets/price-remote/price-remote-favorite/PriceRemoteMaximumFavoriteToaster';
import { Pancakes } from 'Components/widgets/pancakes/Pancakes';
import { PromotionalBanner } from 'Components/widgets/promotional-banner/PromotionalBanner';
import { ResortNotBookingToasterContainer } from 'Components/widgets/resort-not-booking-toaster/ResortNotBookingToasterContainer';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { GET_USER_PERSONALIZATION } from 'Utils/request/requestDictionary';
import { ConfirmationHeader } from 'Components/widgets/confirmation-header/ConfirmationHeader';
import { BookingFooter } from 'Components/widgets/booking-footer/BookingFooter';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { userInfoSelector } from 'Store/user-info/userInfo.selectors';
import { getLeadScore } from 'Store/lead-info/leadInfo.selector';
import { ONE_HOUR_IN_MS } from 'Tracking/tracking-storage-api';
import { DidomiBanner } from 'Tracking/didomi/DidomiBanner';
const FooterContainer = dynamic(
  () => import('Components/layouts/footer/FooterContainer').then((m) => m.FooterContainer),
  { ssr: false },
);
const NavigationBar = dynamic(
  () =>
    import('Components/widgets/navigation/Navigation').then(({ Navigation: Nav }) => ({
      default: userInfoWrapper(Nav),
    })),
  { ssr: false, loading: () => null },
);
type Props = {
  className?: string;
  children?: Node;
  config: CMSContent;
  disableHeader: boolean;
  disableFooter: boolean;
  isMicePage: boolean;
  legalInformations: {
    newsletter: Array<{
      id: number;
      description: string;
      required: boolean;
    }>;
  };
  newsletters: Array<{
    id: string;
    description: string;
  }>;
};

const getLocales = (currentLocale, availableLocales) => {
  const hiddenLocales = (config.get('featureFlipping.hiddenLocales') ?? '')
    .split(',')
    .filter((l) => l !== currentLocale);
  return omit(availableLocales, hiddenLocales);
};

const PAGE_COUNTER = 'PAGE_COUNTER';
const USER_TYPE = 'userType';
export const Layout = memo<Props>(function Layout({
  config: cmsConfig = {} as Partial<CMSContent>,
  // sometimes cmsConfig is undefined at first render witch causes error with destructuring ;
  disableFooter,
  disableHeader,
  className,
  isMicePage,
  legalInformations,
  newsletters,
  children,
}) {
  const appContext = useAppContext();
  const { availableLocales, defaultLocale, locale: currentLocale, googleTrackingId } = appContext;
  const { pageId } = usePageContext();
  const { asPath } = useRouter();
  const pathname = new URI(asPath).pathname();
  const promoBannerRef = useRef();
  const track = useTracking({});
  const userInfo = useSelector(userInfoSelector);
  const leadScore = useSelector(getLeadScore);
  const { tracking } = cmsConfig;
  useEffect(() => {
    const caTracking = {
      connection_status: userStatus(appContext),
      full_visitor_id: appContext.googleTrackingId,
      fidelity_status: userInfo?.loyaltyProgram?.status || '',
      lead_scoring_range: leadScore,
    };
    setInLocalStorage(CA_TRACKING, caTracking, {
      ttl: ONE_HOUR_IN_MS,
    });
  }, [leadScore]);
  const [viewedPageCount, setViewedPageCount] = useState(getFromLocalStorage(PAGE_COUNTER) || 0);
  useEffect(() => {
    const pageCounter = getFromLocalStorage(PAGE_COUNTER) || 0;
    setInLocalStorage(PAGE_COUNTER, pageCounter + 1);
    setViewedPageCount(pageCounter + 1);
  }, [pathname]);
  useEffect(() => {
    if (googleTrackingId && isFeatureFlipped(appContext)('enableUserPersonalization')) {
      const userType = cookies.get(USER_TYPE);

      if (!userType) {
        graphQLClient
          .runNoCacheQuery(
            `query {
          userPersonalization(visitorUuid: "${googleTrackingId}") {
            template
          }
        }`,
            appContext,
            {
              requestName: GET_USER_PERSONALIZATION,
            },
          )
          .then(({ userPersonalization }) => {
            const inThirtyMinutes = new Date(new Date().getTime() + 30 * 60 * 1000);
            cookies.set(USER_TYPE, userPersonalization.template, {
              expires: inThirtyMinutes,
            });
            track({
              event: 'userTypeReady',
              userType: userPersonalization.template,
            });
          });
      }
    }
  }, [pathname, googleTrackingId]);
  const {
    navigationBar,
    promotionalBanner = {
      enabled: false,
    } as Partial<CMSPromotionalBanner>,
    faceting,
    crisisAlert,
  } = cmsConfig;
  addTranslationKeys(cmsConfig);
  const navLanguage = {
    currentLocale,
    defaultLocale,
    locales: getLocales(currentLocale, availableLocales),
  };
  const isConfirmation = pathname === '/confirmation';
  const shouldDisplayPromoBanner =
    !disableHeader && !isMicePage && promotionalBanner.enabled && !isConfirmation;
  const promotionalBannerLink = {
    url: promotionalBanner.link,
  };
  return (
    <Fragment>
      {tracking.optMode !== 'off' && <DidomiBanner optMode={tracking.optMode} />}
      {shouldDisplayPromoBanner && (
        <PromotionalBanner
          className={classnames('relative z-6', `text-${promotionalBanner.color}`)}
          callToAction={promotionalBanner.callToAction}
          closeLabel={promotionalBanner.closeButtonLabel}
          description={promotionalBanner.description}
          disabledPages={promotionalBanner.disabledPages}
          enabledPages={promotionalBanner.enabledPages}
          link={promotionalBannerLink}
          title={promotionalBanner.title}
          ref={promoBannerRef}
        />
      )}
      <div className={classnames('Layout relative table w-full table-fixed', className)}>
        {!disableHeader ? (
          <React.Fragment>
            <Pancakes key={'pancakes-' + pageId}>
              <PriceRemoteMaximumFavoriteToaster />
              <ResortNotBookingToasterContainer />
              <CrisisAlertLinkBanner
                alertDate={crisisAlert?.enabled ? crisisAlert.alertDate : ''}
                className="ms-auto"
                linkLabel={crisisAlert?.link?.label}
                linkUrl={crisisAlert?.link?.url}
              />
              <FTCToasterContainer />
            </Pancakes>
            <div className="absolute inset-x-0 top-0">
              {pathname === '/confirmation' ||
              pathname === '/option/c/200' ||
              pathname === '/option/c/400' ? (
                <ConfirmationHeader contact={navigationBar.contact} />
              ) : (
                <NavigationBar
                  facetingSuggestedRequests={faceting?.suggestedRequests}
                  language={navLanguage}
                  navigation={navigationBar}
                  promoBannerRef={promoBannerRef}
                />
              )}
            </div>
          </React.Fragment>
        ) : null}
        {children}
        {!disableFooter &&
          (['/confirmation', '/option/c/200', '/option/c/400'].includes(pathname) ? (
            <SiteContainer>
              <BookingFooter footer={cmsConfig.footer} className="mt-100" />
            </SiteContainer>
          ) : (
            <FooterContainer
              config={cmsConfig}
              legalInformations={legalInformations}
              newsletters={newsletters}
              viewedPageCount={viewedPageCount}
            />
          ))}
      </div>
      <DataScore />
      <div id={TOASTERS} />
    </Fragment>
  );
});
